import {useEffect, useState} from "react";
import {UserInterface} from "../interfaces/user.interface";
import {getSuiteManagerUserDisplayName, parseJwt} from '../utils/helpers';
import {getGlobalControls} from "../api/AppControlsAPI";
import {fetchUserCompany} from "../api/Auth0API";
import {receiveUserCompany, receiveUserInfo} from "../actions/userInfo";
import {UserRole} from "../interfaces/user.product.interface";
import {AxiosResponse} from "axios";
import Cookies from "js-cookie";

function getUserType(userInfoTemp: UserInterface): string {
  if (userInfoTemp.isFSEmployee) {
    return 'FS Employee';
  }
  if (userInfoTemp.isOnboardUser) {
    return 'Association Onboarding user';
  }
  if (userInfoTemp.isReportsUser) {
    return 'Reports user';
  }
  if (userInfoTemp.isDashboardUser) {
    return 'Dashboard user';
  }
  if (userInfoTemp.fullAccessProfile) {
    return 'Full Access Profile user';
  }
  if (userInfoTemp.isReadOnlyUser) {
    return 'Read Only user';
  }
  return 'All of the above';
}

interface UseAppInitProps {
  isAuthenticated: boolean;
  user: any;
  userProduct: any;
  userInfo?: UserInterface;
  setUserInfo: (user: UserInterface) => void;
  setLoadingUser: (loading: boolean) => void;
  getTokenSilently: () => Promise<string | undefined>;
  dispatch: any;
  config: any;
}

export const useAppInit = ({
  isAuthenticated,
  user,
  userProduct,
  userInfo,
  setUserInfo,
  setLoadingUser,
  getTokenSilently,
  dispatch,
  config
}: UseAppInitProps) => {
  const [userType, setUserType] = useState<string | null>(null);
  useEffect(() => {
    let userInfoTemp: UserInterface = {
      name: '',
      email: '',
      isFSEmployee: false,
      isReportsUser: false,
      isOnboardUser: false,
      globalReportsEnabled: false,
      isDashboardUser: false,
    };
    
    const initializeAuth0User = async () => {
      setLoadingUser(true);
      
      const token = await getTokenSilently();
      if (!token) return;
      
      const parsedToken = parseJwt(token);
      
      // Extract keys
      const findKey = (keyword: string) =>
        Object.keys(parsedToken).filter((key) => key.includes(keyword));
      
      const isFSEmployeeKey = findKey('IsFSEmployee');
      const companyIds = findKey('CompanyId');
      const onboardUserKey = findKey('OnboardUser');
      const fullAccessProfileKey = findKey('FullAccessProfile');
      const isReportUserKey = findKey('ReportsUser');
      const isDashboardUserKey = findKey('DashboardUser');
      const rolesKeys = findKey('Roles');
      
      const roles: string[] = rolesKeys.length ? parsedToken[rolesKeys[0]] : [];
      
      const onboardUser = onboardUserKey.length
        ? !!parsedToken[onboardUserKey[0]]
        : false;
      const fullAccessProfile = fullAccessProfileKey.length
        ? !!parsedToken[fullAccessProfileKey[0]]
        : false;
      const isFSEmployee = isFSEmployeeKey.length
        ? !!parsedToken[isFSEmployeeKey[0]]
        : false;
      const isReportsUser = isReportUserKey.length
        ? !!parsedToken[isReportUserKey[0]]
        : false;
      const isDashboardUser = isDashboardUserKey.length
        ? !!parsedToken[isDashboardUserKey[0]]
        : false;
      
      const baseInfo: Partial<UserInterface> = {
        name: user.name,
        email: user.email?.toLowerCase(),
        isFSEmployee,
        isOnboardUser: onboardUser,
        fullAccessProfile,
        isReportsUser,
        isDashboardUser,
      };
      
      const populateUserInfo = async (info: UserInterface) => {
        const conf = await getGlobalControls();
        info.globalReportsEnabled = conf.reportsEnabled;
        info.auth0Disabled = conf.auth0Disabled;
        info.releaseModalOpen = conf.displayReleaseNotes;
        info.releaseVersion = conf.releaseVersion;
      }
      
      const finalizeUserInfo = async (info: UserInterface) => {
        sessionStorage.setItem('auth_token', token || '');
        setUserInfo(info);
        setLoadingUser(false);
      };
      
      // External user handling
      if (!isFSEmployee && companyIds.length) {
        userInfoTemp = { ...baseInfo } as UserInterface;
        
        const company = await fetchUserCompany(parsedToken[companyIds[0]]);
        userInfoTemp.userCompany = company;
        await populateUserInfo(userInfoTemp);
        dispatch(receiveUserCompany(company, userInfoTemp));
        await finalizeUserInfo(userInfoTemp);
      } else {
        userInfoTemp = {
          name: user.name,
          email: user.email?.toLowerCase(),
          isFSEmployee,
          isReadOnlyUser: roles.includes('read-only')
        } as UserInterface;
        await populateUserInfo(userInfoTemp);
        dispatch(receiveUserInfo(userInfoTemp))
        await finalizeUserInfo(userInfoTemp);
      }
    };
    
    const initializeSuiteManagerUser = async () => {
      setLoadingUser(true);
      
      const paymentsUser = userProduct.paymentsUser;
      const onboardUser = paymentsUser.roles.includes(UserRole.OnboardUser);
      const fullAccessProfile = paymentsUser.roles.includes(UserRole.FullAccess);
      const isReportsUser = paymentsUser.roles.includes(UserRole.ReportsUser);
      const isDashboardUser = paymentsUser.roles.includes(UserRole.DashboardUser);
      const isInternalUser = paymentsUser.userType === 'Internal';
      
      userInfoTemp = {
        name: getSuiteManagerUserDisplayName(userProduct),
        email: userProduct.email.toLowerCase(),
        isFSEmployee: isInternalUser,
        isOnboardUser: onboardUser,
        fullAccessProfile,
        isReportsUser,
        isDashboardUser,
        isReadOnlyUser: paymentsUser.readOnly ?? false,
      };
      
      const userData = {
        name: userInfoTemp.name,
        email: userInfoTemp.email,
        companyId: paymentsUser.mcId,
        userId: paymentsUser.userId,
        userType: paymentsUser.userType,
      };
      sessionStorage.setItem('userData', JSON.stringify(userData));
      
      try {
        const conf = await getGlobalControls();
        userInfoTemp.globalReportsEnabled = conf.reportsEnabled;
        userInfoTemp.auth0Disabled = conf.auth0Disabled;
        userInfoTemp.releaseModalOpen = conf.displayReleaseNotes;
        userInfoTemp.releaseVersion = conf.releaseVersion;

        if (isInternalUser) {
          dispatch(receiveUserInfo(userInfoTemp));
          setUserInfo(userInfoTemp);
          return;
        }

        const company = await fetchUserCompany(String(paymentsUser.mcId));
        userInfoTemp.userCompany = company;

        dispatch(receiveUserCompany(company, userInfoTemp));
        setUserInfo(userInfoTemp);
      } catch (error: any) {
        const status = (error as AxiosResponse).status;
        if ([400, 404].includes(status)) {
          window.location.replace(config.frontstepsPaymentsUrl);
        } else if (status === 401) {
          console.log('Unauthorized');
          sessionStorage.clear();
        }
      } finally {
        setLoadingUser(false);
      }
    };
    
    const init = async () => {
      if (isAuthenticated && !userInfo) {
        if (user) {
          await initializeAuth0User();
        } else if (userProduct?.paymentsUser) {
          await initializeSuiteManagerUser();
        }
        const userType = getUserType(userInfoTemp);
        setUserType(userType)
        Cookies.set('User Type', userType, { path: '/' });
      }
    };
    
    init();
  }, [isAuthenticated, user, userProduct, userInfo]);
  
  return { userType };
};
